// src/functions/liveSession.js
// PulsePlay 2.0 live bus on Firestore. Presenter writes the control state;
// participants subscribe and render; participants write only their own
// responses (write-once locked docs). Dev-open rules assumed.
//
// Firestore layout (doc id = `${org}_${session}_${theme}`):
//   ppsessions/{id}                          -> live control doc (presenter owns)
//   ppsessions/{id}/participants/{userId}
//   ppsessions/{id}/responses/{activityId}_{qIdx}_{userId}   (quiz)
//   ppsessions/{id}/votes/{activityId}_{qIdx}_{userId}       (poll)
//   ppsessions/{id}/words/{autoId}                           (wordcloud)
//   ppsessions/{id}/pins/{autoId}                            (pindrop)

import {
  doc,
  collection,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  increment,
  runTransaction,
  getCountFromServer,
  Timestamp,
} from "firebase/firestore";
import { database } from "../database/firebase";
import { computePoints } from "./scoring";

export function liveId(organizationId, sessionId, themename) {
  return `${organizationId}_${sessionId}_${themename}`;
}

function liveDocRef(id) {
  return doc(database, "ppsessions", id);
}

// ---- Presenter: control state ------------------------------------------

// Create the initial live doc (lobby) ONLY if it doesn't already exist.
// Re-opening the presenter on an in-progress session must NOT reset position.
export async function initLive(id, payload) {
  const ref = liveDocRef(id);
  const snap = await getDoc(ref);
  if (snap.exists()) return snap.data(); // already running/initialized — leave it alone

  await setDoc(
    ref,
    {
      status: "waiting", // waiting | running | ended
      currentIndex: 0, // activity index
      currentQuestionIndex: 0,
      // phase: lobby | countdown | question | timesup | reveal | qleaderboard | finalleaderboard
      phase: "lobby",
      pollReveal: false,
      startedAt: null,
      countdownEndsAt: null, // epoch ms when the 3-2-1 countdown ends
      questionEndsAt: null, // epoch ms when the CURRENT question timer ends
      questionSeconds: 0, // the current question's allotted seconds
      paused: false,
      pausedRemaining: null, // seconds left when paused (presenter left screen)
      updatedAt: serverTimestamp(),
      ...payload,
    },
    { merge: true }
  );
  return null;
}

// Patch the live control doc (presenter actions).
export async function patchLive(id, patch) {
  await updateDoc(liveDocRef(id), { ...patch, updatedAt: serverTimestamp() });
}

const COUNTDOWN_MS = 3000; // 3-2-1

// Start the session -> go into the 3-2-1 countdown for the first question.
export async function startLive(id) {
  await updateDoc(liveDocRef(id), {
    status: "running",
    phase: "countdown",
    currentIndex: 0,
    currentQuestionIndex: 0,
    startedAt: Date.now(),
    countdownEndsAt: Date.now() + COUNTDOWN_MS,
    questionEndsAt: null,
    questionSeconds: 0,
    paused: false,
    pausedRemaining: null,
    updatedAt: serverTimestamp(),
  });
}

// Begin the 3-2-1 countdown for a question (presenter calls on each new question).
export async function beginCountdown(id, { currentIndex, currentQuestionIndex }) {
  await updateDoc(liveDocRef(id), {
    phase: "countdown",
    currentIndex,
    currentQuestionIndex,
    pollReveal: false,
    countdownEndsAt: Date.now() + COUNTDOWN_MS,
    questionEndsAt: null,
    questionSeconds: 0,
    paused: false,
    pausedRemaining: null,
    updatedAt: serverTimestamp(),
  });
}

// Countdown finished -> open the question with its per-question timer (seconds).
export async function openQuestion(id, seconds) {
  const secs = Math.max(1, Number(seconds) || 30);
  await updateDoc(liveDocRef(id), {
    phase: "question",
    questionSeconds: secs,
    questionEndsAt: Date.now() + secs * 1000,
    paused: false,
    pausedRemaining: null,
    updatedAt: serverTimestamp(),
  });
}

// Question time elapsed -> Time's up. Also clears any lingering pause state
// (e.g. set by pauseQuestion() if the presenter's tab closed mid-question and
// a fallback is force-ending it) so a stale `paused: true` doesn't confuse
// the next question.
export async function markTimesUp(id) {
  await updateDoc(liveDocRef(id), {
    phase: "timesup",
    questionEndsAt: null,
    paused: false,
    pausedRemaining: null,
    updatedAt: serverTimestamp(),
  });
}

// Pause the running question timer (presenter left the screen).
export async function pauseQuestion(id) {
  const snap = await getDoc(liveDocRef(id));
  if (!snap.exists()) return;
  const d = snap.data();
  if (d.phase !== "question" || d.paused || !d.questionEndsAt) return;
  const remaining = Math.max(0, Math.round((d.questionEndsAt - Date.now()) / 1000));
  await updateDoc(liveDocRef(id), {
    paused: true,
    pausedRemaining: remaining,
    questionEndsAt: null,
    updatedAt: serverTimestamp(),
  });
}

// Resume the question timer (presenter returned).
export async function resumeQuestion(id) {
  const snap = await getDoc(liveDocRef(id));
  if (!snap.exists()) return;
  const d = snap.data();
  if (d.phase !== "question" || !d.paused) return;
  const remaining = Math.max(1, Number(d.pausedRemaining) || 1);
  await updateDoc(liveDocRef(id), {
    paused: false,
    pausedRemaining: null,
    questionEndsAt: Date.now() + remaining * 1000,
    updatedAt: serverTimestamp(),
  });
}

export async function endLive(id) {
  await updateDoc(liveDocRef(id), {
    status: "ended",
    phase: "finalleaderboard",
    questionEndsAt: null,
    updatedAt: serverTimestamp(),
  });
}

// Subscribe to the live control doc. cb(data|null). Returns unsubscribe.
export function subscribeLive(id, cb) {
  return onSnapshot(liveDocRef(id), (snap) => cb(snap.exists() ? snap.data() : null));
}

export async function getLiveOnce(id) {
  const snap = await getDoc(liveDocRef(id));
  return snap.exists() ? snap.data() : null;
}

// ---- Presence ----------------------------------------------------------

export async function joinParticipant(id, userId, name) {
  // Participants often land here before the presenter has ever opened the
  // presenter screen (e.g. a shared join link opened early) — in that case
  // the live control doc doesn't exist yet. initLive() is idempotent
  // (no-ops if the presenter already created it), so it's safe to call from
  // both sides; this just guarantees the control doc exists either way.
  await initLive(id, {});
  const ref = doc(database, "ppsessions", id, "participants", String(userId));
  const existing = await getDoc(ref);
  await setDoc(
    ref,
    {
      userId: String(userId),
      name: name || String(userId),
      joinedAt: serverTimestamp(),
      lastSeen: serverTimestamp(),
      points: existing.exists() ? existing.data().points || 0 : 0,
      correctCount: existing.exists() ? existing.data().correctCount || 0 : 0,
      totalTimeMs: existing.exists() ? existing.data().totalTimeMs || 0 : 0,
      answeredCount: existing.exists() ? existing.data().answeredCount || 0 : 0,
    },
    { merge: true }
  );
}

export async function heartbeat(id, userId) {
  const ref = doc(database, "ppsessions", id, "participants", String(userId));
  // updateDoc (not setDoc merge) — a heartbeat must only ever touch a doc
  // that already exists, never create one. A leftover open browser tab
  // from a previous test keeps firing this every 15s regardless of
  // anything the admin does; with setDoc({merge:true}) that silently
  // resurrected a bare, nameless participant doc after a session reset
  // deleted it, which is exactly why "joined" counts crept back up from 0
  // instead of staying reset. updateDoc throws on a missing doc instead —
  // caught and ignored at the call site (play.jsx), so a reset now stays
  // reset for real.
  await updateDoc(ref, { lastSeen: serverTimestamp() });
}

// Unscoped — re-delivers EVERY participant doc on every single participant's
// write. Fine for a one-off admin page opened by a single browser, but never
// use this where many clients could have it open at once (e.g. every
// participant's own screen) — that's an O(participants^2) read/render cost
// at scale. Prefer subscribeTopParticipants + getParticipantRank below.
export function subscribeParticipants(id, cb) {
  const col = collection(database, "ppsessions", id, "participants");
  return onSnapshot(col, (snap) => cb(snap.docs.map((d) => d.data())));
}

// Bounded leaderboard listener — only the top N by points, regardless of how
// many participants exist. Safe for every participant's own screen to watch
// simultaneously since the result size never grows with the crowd.
export function subscribeTopParticipants(id, topN, cb) {
  const col = collection(database, "ppsessions", id, "participants");
  const q = query(col, orderBy("points", "desc"), limit(topN));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

// A single participant's own doc — one listener per participant, never a
// fan-out risk no matter how many participants there are.
export function subscribeMyParticipant(id, userId, cb) {
  const ref = doc(database, "ppsessions", id, "participants", String(userId));
  return onSnapshot(ref, (snap) => cb(snap.exists() ? snap.data() : null));
}

// One-time (non-live) fetch of the full roster — for admin views (CSV
// export, "who's joined" list) that need everyone but don't need a
// permanent listener on the whole collection re-firing on every write.
export async function getParticipantsOnce(id) {
  const col = collection(database, "ppsessions", id, "participants");
  const snap = await getDocs(col);
  return snap.docs.map((d) => d.data());
}

// How many participants currently have strictly more points than `myPoints`
// — i.e. this participant's rank minus 1 — via a server-side count
// aggregation instead of downloading every participant document.
export async function getParticipantRank(id, myPoints) {
  const col = collection(database, "ppsessions", id, "participants");
  const q = query(col, where("points", ">", myPoints || 0));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// Total participant count via a server-side count aggregation — not a
// stored running total. Deliberately not an incremented counter field: a
// counter can only ever be as correct as every single increment that was
// ever supposed to happen (join-before-control-doc-exists, a dropped
// write, a future code path that creates a participant doc some other
// way — any one of those silently desyncs it forever). This is instead
// computed fresh from the real collection every call, so it can never
// drift, at the cost of needing to be polled rather than truly live.
export async function getParticipantCount(id) {
  const col = collection(database, "ppsessions", id, "participants");
  const snap = await getCountFromServer(col);
  return snap.data().count;
}

// "Currently here" rather than "ever joined" — counts participants whose
// heartbeat() has landed within the last `windowMs` (default 45s = 3 missed
// 15s heartbeats before someone's considered gone, tolerant of normal
// jitter/background-tab throttling without actually waiting too long).
// Firestore has no disconnect-detection hook (that's RTDB's onDisconnect()),
// so this is the standard heartbeat+staleness-window approximation instead —
// a single-field range filter, no composite index needed, same
// count-aggregation cost profile as getParticipantCount above regardless of
// how many participants exist. Has to be polled (not onSnapshot) since the
// cutoff is a constantly-moving target, not a fixed value.
export async function getActiveParticipantCount(id, windowMs = 45000) {
  const col = collection(database, "ppsessions", id, "participants");
  const cutoff = Timestamp.fromMillis(Date.now() - windowMs);
  const q = query(col, where("lastSeen", ">", cutoff));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

// ---- Quiz responses (write-once locked) --------------------------------

export async function getMyQuizResponse(id, activityId, qIdx, userId) {
  const ref = doc(
    database,
    "ppsessions",
    id,
    "responses",
    `${activityId}_${qIdx}_${userId}`
  );
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

// All of this participant's quiz responses for the session, in one round
// trip — used to resume a self-paced quiz after a page refresh instead of
// re-fetching per question (which would re-introduce N sequential reads).
// Keyed by `${activityId}_${qIdx}` to match submitQuizAnswer's doc id shape.
export async function getMyQuizResponses(id, userId) {
  const col = collection(database, "ppsessions", id, "responses");
  const q = query(col, where("userId", "==", String(userId)));
  const snap = await getDocs(q);
  const byKey = {};
  snap.docs.forEach((d) => {
    const data = d.data();
    byKey[`${data.activityId}_${data.qIdx}`] = data;
  });
  return byKey;
}

// Submit a quiz answer. Locked: if a doc already exists we do NOT overwrite.
export async function submitQuizAnswer(id, { activityId, qIdx, userId, name, optionIndex, correct, points, timeMs }) {
  const ref = doc(
    database,
    "ppsessions",
    id,
    "responses",
    `${activityId}_${qIdx}_${userId}`
  );
  const pref = doc(database, "ppsessions", id, "participants", String(userId));
  const data = {
    activityId,
    qIdx,
    userId: String(userId),
    name: name || String(userId),
    optionIndex,
    correct: !!correct,
    points: points || 0,
    timeMs: timeMs || 0,
    submittedAt: Date.now(),
  };
  // Transactional: the "already answered?" check and the write-once response
  // + participant aggregate bump happen atomically. Without this, two
  // near-simultaneous calls (e.g. a double-tapped Submit button, or a slow
  // network making someone tap again) could both pass the existence check
  // before either commits — the response doc still ends up as a single row
  // (same deterministic id either way), but the aggregate points/time/
  // answeredCount would get incremented twice for what's really one answer,
  // silently drifting the leaderboard above the true per-question sum.
  // Firestore retries the whole transaction on conflict, so the second
  // caller re-reads, sees the doc now exists, and backs off cleanly instead
  // of double-incrementing.
  return runTransaction(database, async (tx) => {
    // Firestore transactions require every read to happen before any write,
    // so both existence checks come first.
    const existing = await tx.get(ref);
    if (existing.exists()) return existing.data(); // already locked
    // Only bump the aggregate on a participant doc that's actually still
    // there. A stale tab left open from before a session reset can still
    // fire a submit for a question that was live before the reset — without
    // this check, the merge-write below would silently resurrect a bare
    // participant doc (just points/correctCount/etc., no name/userId/
    // joinedAt), which is exactly what showed up as a "?" avatar after
    // reset. The response itself still gets recorded either way; only the
    // orphaned stub creation is what's being prevented.
    const pdoc = await tx.get(pref);

    tx.set(ref, data);
    if (pdoc.exists()) {
      tx.set(
        pref,
        {
          points: increment(data.points || 0),
          correctCount: increment(data.correct ? 1 : 0),
          totalTimeMs: increment(data.timeMs || 0),
          answeredCount: increment(1),
        },
        { merge: true }
      );
    }
    return data;
  });
}

export function subscribeQuizResponses(id, activityId, qIdx, cb) {
  const col = collection(database, "ppsessions", id, "responses");
  const q = query(col, where("activityId", "==", activityId), where("qIdx", "==", qIdx));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

// Fastest correct answers for ONE question — fully server-side sorted and
// bounded via orderBy+limit. Never downloads more than topN docs regardless
// of how many thousand people answered this question (unlike deriving this
// from subscribeQuizResponses above, which requires the full per-question
// response set for the bar-chart's per-option counts and would be the wrong
// thing to reuse here). Requires a Firestore composite index on
// (activityId ==, qIdx ==, correct ==, timeMs asc) over `responses` — the
// console gives you a direct "create index" link the first time this runs
// if it isn't already there.
export function subscribeFastestCorrect(id, activityId, qIdx, topN, cb) {
  const col = collection(database, "ppsessions", id, "responses");
  const q = query(
    col,
    where("activityId", "==", activityId),
    where("qIdx", "==", qIdx),
    where("correct", "==", true),
    orderBy("timeMs", "asc"),
    limit(topN)
  );
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

// ---- Poll votes (write-once) -------------------------------------------

export async function getMyVote(id, activityId, qIdx, userId) {
  const ref = doc(database, "ppsessions", id, "votes", `${activityId}_${qIdx}_${userId}`);
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null;
}

export async function submitVote(id, { activityId, qIdx, userId, name, optionIndex }) {
  const ref = doc(database, "ppsessions", id, "votes", `${activityId}_${qIdx}_${userId}`);
  const existing = await getDoc(ref);
  if (existing.exists()) return existing.data();
  const data = {
    activityId,
    qIdx,
    userId: String(userId),
    name: name || String(userId),
    optionIndex,
    submittedAt: Date.now(),
  };
  await setDoc(ref, data);
  return data;
}

export function subscribeVotes(id, activityId, qIdx, cb) {
  const col = collection(database, "ppsessions", id, "votes");
  const q = query(col, where("activityId", "==", activityId), where("qIdx", "==", qIdx));
  return onSnapshot(q, (snap) => cb(snap.docs.map((d) => d.data())));
}

// ---- Word cloud --------------------------------------------------------

export async function submitWord(id, { activityId, qIdx, userId, name, text }) {
  const ref = doc(collection(database, "ppsessions", id, "words"));
  const data = {
    activityId,
    qIdx,
    userId: String(userId),
    name: name || String(userId),
    text,
    removed: false,
    submittedAt: Date.now(),
  };
  await setDoc(ref, data);
  return ref.id;
}

export async function removeWord(id, wordDocId) {
  const ref = doc(database, "ppsessions", id, "words", wordDocId);
  await setDoc(ref, { removed: true }, { merge: true });
}

export function subscribeWords(id, activityId, qIdx, cb) {
  const col = collection(database, "ppsessions", id, "words");
  const q = query(col, where("activityId", "==", activityId), where("qIdx", "==", qIdx));
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ docId: d.id, ...d.data() })))
  );
}

// ---- Pin drop ----------------------------------------------------------

// Standard ray-casting point-in-polygon test.
function pointInPolygon(px, py, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    const intersects =
      (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

// Point-in-polygon check against the admin-traced drop zones. Any zone
// counts as correct (no single "right" zone) — mirrors the admin UI's
// "all drawn zones are valid" rule.
function pinIsInsideAnyPolygon(x, y, polygons) {
  return (polygons || []).some((poly) => pointInPolygon(x, y, poly.points || []));
}

// Submit a pin drop. Each pin is judged independently and scored the same
// way as a quiz correct answer (fixed pointValue) — multiple correct pins
// from the same participant stack points, same as multiple correct quiz
// answers would. timeMs is measured by the caller (time since the question/
// image appeared) and recorded for the fastest-correct leaderboard.
export async function submitPin(id, { activityId, qIdx, userId, name, x, y, polygons, pointValue, scoringMode, timerSeconds, timeMs }) {
  const ref = doc(collection(database, "ppsessions", id, "pins"));
  const correct = pinIsInsideAnyPolygon(x, y, polygons);
  const points = computePoints({ correct, timeMs, timerSeconds, scoringMode, pointValue });
  const data = {
    activityId,
    qIdx,
    userId: String(userId),
    name: name || String(userId),
    x,
    y,
    correct,
    points,
    timeMs: timeMs || 0,
    confirmedAt: Date.now(),
  };
  // Write the pin and bump the participant aggregate in parallel — same
  // reasoning as submitQuizAnswer (atomic increment() means no read-then-add
  // round-trip, and the two writes don't depend on each other).
  const pref = doc(database, "ppsessions", id, "participants", String(userId));
  // Same reasoning as submitQuizAnswer: only bump the aggregate on a
  // participant doc that's actually still there, so a stale tab submitting
  // after a session reset can't resurrect a bare, nameless stub.
  const pdoc = await getDoc(pref);
  await Promise.all([
    setDoc(ref, data),
    pdoc.exists()
      ? setDoc(
          pref,
          {
            points: increment(points),
            correctCount: increment(correct ? 1 : 0),
            totalTimeMs: increment(timeMs || 0),
            answeredCount: increment(1),
          },
          { merge: true }
        ).catch(() => {}) // non-fatal
      : Promise.resolve(),
  ]);

  return { docId: ref.id, ...data };
}

export function subscribePins(id, activityId, qIdx, cb) {
  const col = collection(database, "ppsessions", id, "pins");
  const q = query(col, where("activityId", "==", activityId), where("qIdx", "==", qIdx));
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ docId: d.id, ...d.data() })))
  );
}

// Fastest correct pins for ONE question — same reasoning/index requirement
// as subscribeFastestCorrect above, just against the `pins` collection.
export function subscribeFastestCorrectPins(id, activityId, qIdx, topN, cb) {
  const col = collection(database, "ppsessions", id, "pins");
  const q = query(
    col,
    where("activityId", "==", activityId),
    where("qIdx", "==", qIdx),
    where("correct", "==", true),
    orderBy("timeMs", "asc"),
    limit(topN)
  );
  return onSnapshot(q, (snap) =>
    cb(snap.docs.map((d) => ({ docId: d.id, ...d.data() })))
  );
}

// ---- Reset: wipe all played/runtime data for a session ------------------
// Deletes participants, responses, votes, words, pins, and the live control
// doc. Config in MySQL (activities/questions) is NOT touched. Returns counts.
export async function resetLiveSession(id) {
  const subcollections = ["participants", "responses", "votes", "words", "pins"];
  const result = {};

  for (const sub of subcollections) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(collection(database, "ppsessions", id, sub));
    let n = 0;
    // Delete sequentially (batches would be faster but this keeps it simple + safe).
    for (const d of snap.docs) {
      // eslint-disable-next-line no-await-in-loop
      await deleteDoc(d.ref);
      n += 1;
    }
    result[sub] = n;
  }

  // Finally delete the live control doc itself.
  try {
    await deleteDoc(liveDocRef(id));
    result.live = 1;
  } catch (e) {
    result.live = 0;
  }

  return result;
}

// ---- Report: read ALL played data once (no listeners) -------------------
// Pulls every subcollection for the session so the Review page can build a
// CSV export. Read-only; nothing is modified. Returns plain arrays of the
// stored docs (participants, responses, votes, words, pins).
export async function fetchAllPlayedData(id) {
  const subs = ["participants", "responses", "votes", "words", "pins"];
  const out = {};
  for (const sub of subs) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(collection(database, "ppsessions", id, sub));
    out[sub] = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
  }
  return out;
}
